import React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Route, Server, CheckCircle2, XCircle, ArrowRight,
  Shuffle, BarChart3, Zap, GitBranch, Shield, Activity,
  Lock,
} from "lucide-react";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName as getProviderNameUtil } from "@/shared/utils";

const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

.rt-root {
  min-height: 100vh;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: #e2f4ff;
  padding: 0 0 48px;
}
.rt-root * { box-sizing: border-box; }

.rt-h1 { color: #e2f4ff; font-size: 1.6rem; font-weight: 800; letter-spacing: -0.03em; }
.rt-sub { color: #2a4a65; font-size: 13px; font-weight: 500; margin-top: 4px; }

/* ── Rule Card ── */
.rt-card {
  background: linear-gradient(145deg, rgba(10,22,46,0.96), rgba(8,18,40,0.98));
  border: 1px solid rgba(34,211,238,0.08);
  border-radius: 26px; padding: 0;
  overflow: hidden; position: relative;
  will-change: transform;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.28s cubic-bezier(0.34,1.56,0.64,1);
}
.rt-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, transparent 0%, #06b6d4 40%, #22d3ee 65%, transparent 100%);
  opacity: 0; transition: opacity 0.3s ease;
}
.rt-card::after {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(ellipse 60% 50% at 20% 0%, rgba(34,211,238,0.06), transparent 65%);
  opacity: 0; transition: opacity 0.3s ease; pointer-events: none;
}
.rt-card:hover {
  border-color: rgba(34,211,238,0.28);
  box-shadow: 0 0 60px rgba(34,211,238,0.1), 0 24px 60px rgba(0,0,0,0.5);
  transform: translateY(-4px);
}
.rt-card:hover::before, .rt-card:hover::after { opacity: 1; }

/* Priority stripe */
.rt-stripe {
  position: absolute; left: 0; top: 0; bottom: 0; width: 4px;
  border-radius: 0 4px 4px 0;
}

/* Card inner */
.rt-inner { padding: 26px 28px; position: relative; z-index: 1; }

/* Icon */
.rt-icon {
  width: 50px; height: 50px; border-radius: 16px; flex-shrink: 0;
  background: linear-gradient(135deg, #06b6d4, #0891b2);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 24px rgba(6,182,212,0.3);
  position: relative; overflow: hidden;
  transition: box-shadow 0.28s ease, transform 0.28s cubic-bezier(0.34,1.56,0.64,1);
}
.rt-card:hover .rt-icon {
  box-shadow: 0 0 40px rgba(6,182,212,0.5);
  transform: scale(1.08) rotate(-6deg);
}
.rt-icon::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(145deg, rgba(255,255,255,0.12), transparent);
}

/* Name */
.rt-name { color: #e2f4ff; font-size: 16px; font-weight: 800; letter-spacing: -0.01em; }
.rt-pri-badge {
  display: inline-flex; align-items: center; gap: 5px; margin-top: 5px;
  background: rgba(34,211,238,0.07); border: 1px solid rgba(34,211,238,0.16);
  border-radius: 8px; padding: 3px 10px;
  font-size: 10px; font-weight: 800; color: #3a6080;
  font-family: 'JetBrains Mono', monospace; letter-spacing: 0.04em;
}

/* Status pill */
.rt-status {
  display: inline-flex; align-items: center; gap: 7px;
  border-radius: 999px; padding: 7px 18px;
  font-size: 11px; font-weight: 800; letter-spacing: 0.04em; flex-shrink: 0;
  transition: box-shadow 0.2s ease;
}
.rt-active   { background: rgba(16,185,129,0.1);  color: #34d399; border: 1px solid rgba(16,185,129,0.25); }
.rt-active:hover { box-shadow: 0 0 18px rgba(16,185,129,0.2); }
.rt-inactive { background: rgba(100,116,139,0.1); color: #94a3b8; border: 1px solid rgba(100,116,139,0.2); }

/* Divider */
.rt-div { height: 1px; background: linear-gradient(90deg, transparent, rgba(34,211,238,0.12), transparent); margin: 20px 0; }

/* Meta grid */
.rt-meta { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
@media (max-width: 860px) { .rt-meta { grid-template-columns: repeat(2,1fr); } }
@media (max-width: 480px) { .rt-meta { grid-template-columns: 1fr 1fr; } }

.rt-meta-lbl { color: #2a4a65; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px; }
.rt-meta-val { color: #e2f4ff; font-size: 13px; font-weight: 700; text-transform: capitalize; }

/* Mode chip */
.rt-mode {
  display: inline-flex; align-items: center; gap: 7px;
  background: rgba(34,211,238,0.07); border: 1px solid rgba(34,211,238,0.2);
  border-radius: 10px; padding: 6px 13px;
  font-size: 12px; font-weight: 800; color: #22d3ee;
  transition: all 0.22s ease;
}
.rt-mode:hover { background: rgba(34,211,238,0.13); box-shadow: 0 0 16px rgba(34,211,238,0.18); }

/* Failover */
.rt-failover { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 800; }
.rt-failover.on { color: #34d399; }
.rt-failover.off { color: #2a4a65; }

/* Provider sequence */
.rt-seq-lbl { color: #2a4a65; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
.rt-seq { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }

.rt-pchip {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(34,211,238,0.05); border: 1px solid rgba(34,211,238,0.14);
  border-radius: 12px; padding: 8px 15px;
  font-size: 12px; font-weight: 700; color: #5a8aaa;
  cursor: default; will-change: transform;
  transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1);
}
.rt-pchip:hover { border-color: rgba(34,211,238,0.4); color: #22d3ee; background: rgba(34,211,238,0.09); transform: translateY(-2px); box-shadow: 0 6px 18px rgba(34,211,238,0.12); }

.rt-num {
  width: 22px; height: 22px; border-radius: 7px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 900; font-family: 'JetBrains Mono', monospace;
  background: rgba(34,211,238,0.1); border: 1px solid rgba(34,211,238,0.2); color: #22d3ee;
}

.rt-arrow { color: rgba(34,211,238,0.2); flex-shrink: 0; transition: color 0.2s; }
.rt-arrow:hover { color: rgba(34,211,238,0.5); }

/* Empty */
.rt-empty {
  background: linear-gradient(145deg, rgba(10,22,46,0.6), rgba(8,18,40,0.6));
  border: 1px dashed rgba(34,211,238,0.12); border-radius: 26px; padding: 80px 24px; text-align: center;
}
.rt-empty-icon {
  width: 72px; height: 72px; border-radius: 22px;
  background: rgba(34,211,238,0.05); border: 1px solid rgba(34,211,238,0.1);
  display: flex; align-items: center; justify-content: center; margin: 0 auto 20px;
}

/* Skeleton */
.rt-skel { background: linear-gradient(145deg, rgba(10,22,46,0.7), rgba(8,18,40,0.7)); border: 1px solid rgba(34,211,238,0.06); border-radius: 26px; padding: 28px; }
@keyframes skel-pulse { 0%,100%{opacity:0.3} 50%{opacity:0.7} }
.rt-skel-line { height: 13px; border-radius: 8px; background: rgba(34,211,238,0.08); animation: skel-pulse 1.6s ease-in-out infinite; }

/* Responsive */
@media (max-width: 600px) { .rt-h1 { font-size: 1.3rem; } .rt-inner { padding: 18px 18px; } }
`;

const MODE_ICONS = { priority: Zap, round_robin: Shuffle, weighted: BarChart3, least_load: Server };
const STRIPE_COLORS = ["#06b6d4", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#ec4899"];

export default function MerchantRouting() {
  const { language, t } = useLanguage();

  const { data: rules = [], isLoading } = useQuery({ queryKey: ["routing-rules"], queryFn: async () => [] });
  const { data: providers = [] } = useQuery({ queryKey: ["providers"], queryFn: async () => [] });

  const getProviderName = (code) => {
    const p = providers.find((p) => p.code === code);
    return p ? getProviderNameUtil(p, language) : code;
  };

  return (
    <MerchantLayout currentPage="MerchantRouting" merchantName="Royal Gaming" merchantCode="MRC001">
      <style>{STYLE}</style>
      <div className="rt-root">
        <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -16, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: "flex", alignItems: "center", gap: 16 }}
          >
            <div style={{
              width: 50, height: 50, borderRadius: 16, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "linear-gradient(135deg, #06b6d4, #0891b2)",
              boxShadow: "0 8px 28px rgba(6,182,212,0.4)",
              position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(145deg, rgba(255,255,255,0.12), transparent)" }} />
              <GitBranch size={22} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
            </div>
            <div>
              <h1 className="rt-h1">{t("routingRulesTitle") || "Routing Rules"}</h1>
              <p className="rt-sub">{t("configureProviderSelection") || "View how transactions are routed to providers"}</p>
            </div>
          </motion.div>

          {/* Rules list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <motion.div key={i} className="rt-skel"
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}>
                  <div className="rt-skel-line" style={{ width: "30%", marginBottom: 16 }} />
                  <div className="rt-skel-line" style={{ width: "55%", marginBottom: 10 }} />
                  <div className="rt-skel-line" style={{ width: "40%" }} />
                </motion.div>
              ))
            ) : rules.length === 0 ? (
              <motion.div className="rt-empty" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                <div className="rt-empty-icon">
                  <GitBranch size={30} style={{ color: "#1e3a52" }} />
                </div>
                <p style={{ color: "#2a4a65", fontSize: 14, fontWeight: 700, marginBottom: 6 }}>{t("noRoutingRules") || "No routing rules configured"}</p>
                <p style={{ color: "#1e3a50", fontSize: 13, fontWeight: 500 }}>Routing rules will appear here once configured by your administrator.</p>
              </motion.div>
            ) : (
              rules.map((rule, i) => {
                const ModeIcon = MODE_ICONS[rule.routing_mode] || Zap;
                let seq = [];
                try { seq = JSON.parse(rule.provider_sequence || "[]"); } catch {}
                const isActive = rule.status === "active";
                const stripeColor = STRIPE_COLORS[i % STRIPE_COLORS.length];

                return (
                  <motion.div key={rule.id} className="rt-card"
                    initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{ delay: i * 0.07, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ y: -4 }}
                  >
                    <div className="rt-stripe" style={{ background: `linear-gradient(180deg, ${stripeColor}, ${stripeColor}40, transparent)` }} />

                    <div className="rt-inner">
                      {/* Header */}
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                          <div className="rt-icon">
                            <Route size={22} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
                          </div>
                          <div>
                            <div className="rt-name">{rule.name}</div>
                            <div className="rt-pri-badge">
                              <Activity size={10} />
                              Priority #{rule.priority}
                            </div>
                          </div>
                        </div>
                        <div className={`rt-status ${isActive ? "rt-active" : "rt-inactive"}`}>
                          {isActive ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                          {rule.status}
                        </div>
                      </div>

                      <div className="rt-div" />

                      {/* Meta grid */}
                      <div className="rt-meta">
                        <div>
                          <div className="rt-meta-lbl">Transaction Type</div>
                          <div className="rt-meta-val">{rule.transaction_type || "All"}</div>
                        </div>
                        <div>
                          <div className="rt-meta-lbl">Payment Method</div>
                          <div className="rt-meta-val">{rule.payment_method?.replace(/_/g, " ") || "All"}</div>
                        </div>
                        <div>
                          <div className="rt-meta-lbl">Routing Mode</div>
                          <div className="rt-mode">
                            <ModeIcon size={13} />
                            {rule.routing_mode?.replace(/_/g, " ") || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="rt-meta-lbl">Failover</div>
                          <div className={`rt-failover ${rule.failover_enabled ? "on" : "off"}`}>
                            <Shield size={13} />
                            {rule.failover_enabled ? "Enabled" : "Disabled"}
                          </div>
                        </div>
                      </div>

                      {seq.length > 0 && (
                        <>
                          <div className="rt-div" />
                          <div>
                            <div className="rt-seq-lbl">
                              <Server size={12} />
                              Provider Sequence
                            </div>
                            <div className="rt-seq">
                              {seq.map((code, idx) => (
                                <React.Fragment key={code}>
                                  <motion.span className="rt-pchip"
                                    initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.07 + idx * 0.05 + 0.2 }}
                                  >
                                    <div className="rt-num">{idx + 1}</div>
                                    <Server size={12} />
                                    {getProviderName(code)}
                                  </motion.span>
                                  {idx < seq.length - 1 && (
                                    <span className="rt-arrow"><ArrowRight size={16} /></span>
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </MerchantLayout>
  );
}